import {
    Dispatch,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import { createWizard } from "../../../../Components/Utils/Wizard";
import StorageManagerCapys from "../../../../Tools/Storage";
import {
    IParcelaBoletos,
    IParcelaBoletosResponseSienge,
    IParcelas,
} from "../../../../Interfaces/Cobranca/ParcelasAbertas/ParcelasAbertas";
import SelecaoParcelas from "./SelecaoParcelas";
import CondicoesPagamento from "./CondicoesPagamento";
import SimularParcela from "./SimularParcela";
import {
    IDescontos,
    IGerarNovoFluxoRequest,
    IIndicadoresParcelasRenegociacao,
    IParcelaGeradaNovoFluxo,
    IParcelasComDescontos,
} from "../../../../Interfaces/Cobranca/Renegociacao/RenegociacaoInterfaces";
import { arrayEquality } from "../../../../Tools/ProcessingTools";
import { ListarParcelasBoletosController } from "../../../../Controllers/Cobranca/ParcelasAbertas/ParcelasAbertasController";
import { useHistoricoContext } from "../../../../Context/Historico";
import { IParcelaModelo } from "../../../../Interfaces/Cobranca/Renegociacao/ModeloRenegociacao/ModeloRenegociacaoInterface";
import { RealizarRenegociacaoSiengeController } from "../../../../Controllers/Cobranca/RenegociacaoSienge/RenegociacaoSiengeController";
import { useAtendimentoPage } from "../../../../Context/AtendimentoPage";
import useAsyncCallback from "../../../../hooks/useAsyncCallback";

type TStepKeys = "selecParcelas" | "condicoesPagamento" | "simularParcela";

interface IRegistrarAcordoSienge {
    goToStep: (step: TStepKeys) => void;
    selecionarParcelas: (parcelasSelecionadas: Array<any>) => void;
    lstSelecionados: IParcelaBoletos[] | undefined;
    setLstSelecionados: (
        value: SetStateAction<IParcelaBoletos[] | undefined>
    ) => void;
    lstSelecionadosParcelasGeradas: IParcelaBoletos[] | undefined;
    setLstSelecionadosParcelasGeradas: Dispatch<
        SetStateAction<IParcelaBoletos[] | undefined>
    >;
    step?: TStepKeys;
    parcelaReq: {
        DT_CALCULO: string;
    };
    setParcelaReq: Dispatch<
        SetStateAction<{
            DT_CALCULO: string;
        }>
    >;
    lstTodasParcelas: {
        get: () => Promise<IParcelaBoletos[]>;
    };
    SalvarSimulacao(formData: any): void;
    RealizarRenegociacaoSiengeLoading: boolean;
}
interface ICalculoNovoFluxoRequest {
    parcelas: Array<IParcelaModelo>;
    request: IGerarNovoFluxoRequest;
}

interface IRenegociacaoCache {
    step?: TStepKeys;
    lstParcelasSelecionadas?: Array<any>;
    lstTodasParcelas?: Array<IParcelaBoletos>;
    lstParcelasSelecionadasComDescontos?: Array<IParcelasComDescontos>;
    indicadores?: IIndicadoresParcelasRenegociacao | undefined;
    descontos?: IDescontos;
    parcelasModeloMap?: Array<[string, Array<IParcelaModelo>]>;
    calculoNovoFluxoRequest?: ICalculoNovoFluxoRequest;
    novoFluxo?: Array<IParcelaGeradaNovoFluxo>;
}

interface IRegistrarAcordoProps extends IParcelas {
    idCaso: number;
    idProduto: number;
    setShow: (show: boolean) => void;
}

const RegistrarAcordoSiengeContext = createContext<IRegistrarAcordoSienge>(
    {} as IRegistrarAcordoSienge
);

export default function RegistrarAcordoSienge(props: IRegistrarAcordoProps) {
    const casheManager = useRef(
        new StorageManagerCapys<IRenegociacaoCache>(
            `RENEGOCIACAO_SIENGE_${props.idCaso}_${props.NR_EMPRESA}_${props.DS_OBRA}_${props.NR_VENDA}`,
            "session"
        )
    );

    const { ReloadHistorico } = useHistoricoContext();
    const { Atendimento } = useAtendimentoPage();

    const cashe = casheManager.current.get();

    const lstParcelas = useRef(cashe?.lstTodasParcelas);

    const [lstSelecionados, setLstSelecionados] =
        useState<Array<IParcelaBoletosResponseSienge>>();

    const [lstSelecionadosParcelasGeradas, setLstSelecionadosParcelasGeradas] =
        useState<Array<IParcelaBoletosResponseSienge>>();

    const [parcelaReq, setParcelaReq] = useState({
        DT_CALCULO: new Date().toISOString().substring(0, 10),
    });
    const etapas: Array<TStepKeys> = [
        "selecParcelas",
        "condicoesPagamento",
        "simularParcela",
    ];

    const { Wizard, WizardStep, useWizard } = createWizard(etapas);

    const wizard = useWizard({
        startAtStep: cashe?.step,
    });

    const { changeStep, step, resetStep } = wizard;
    const { goTo: goToStep } = changeStep;

    const etapasInfo = useMemo(
        () => ({
            selecParcelas: {
                title: "Seleção de Parcelas",
                reset: () => {},
            },
            condicoesPagamento: {
                title: "Condições de pagamento",
                reset: () => {},
            },
            simularParcela: {
                title: "Simular Parcela",
                reset: () => {},
            },
        }),
        []
    );

    const [lstParcelasSelecionadas, setLstParcelasSelecionadas] = useState(
        cashe?.lstParcelasSelecionadas
    );

    const resetPosStep = useCallback(
        (step: TStepKeys) => {
            resetStep(step);
            const indexEtapa = etapas.findIndex((etapa) => etapa === step);
            for (let index = indexEtapa + 1; index < etapas.length; index++) {
                const etapa = etapas[index];
                etapasInfo?.[etapa]?.reset?.();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [resetStep, etapasInfo]
    );

    const selecionarParcelas = useCallback(
        (parcelasSelecionadas: Array<IParcelasComDescontos>) => {
            if (
                !lstParcelasSelecionadas ||
                !arrayEquality(parcelasSelecionadas, lstParcelasSelecionadas)
            ) {
                setLstParcelasSelecionadas(parcelasSelecionadas);
                resetPosStep("selecParcelas");
            }
        },
        [lstParcelasSelecionadas, resetPosStep]
    );

    const lstTodasParcelas = useMemo(
        () => ({
            get: async () => {
                if (lstParcelas.current) return lstParcelas.current;
                else {
                    return ListarParcelasBoletosController({
                        NR_EMPRESA: props.NR_EMPRESA,
                        DS_OBRA: props.DS_OBRA,
                        NR_VENDA: props.NR_VENDA,
                        ...parcelaReq,
                    }).then((parcelas) => {
                        lstParcelas.current = parcelas;
                        return parcelas;
                    });
                }
            },
        }),
        [
            lstParcelas,
            props.NR_EMPRESA,
            props.DS_OBRA,
            props.NR_VENDA,
            parcelaReq,
        ]
    );

    const {
        execute: RealizarRenegociacaoSienge,
        loading: RealizarRenegociacaoSiengeLoading,
    } = useAsyncCallback(RealizarRenegociacaoSiengeController);

    function SalvarSimulacao(formData: any) {
        if (lstSelecionados && lstSelecionadosParcelasGeradas) {
            return RealizarRenegociacaoSienge(Atendimento?.ID_CASO ?? 0, {
                billReceivableId: Number(
                    Atendimento?.ID_CHAVE_ERP_VENDA?.split("_")[2]
                ),
                remadeDate: parcelaReq.DT_CALCULO,
                calculationDate: parcelaReq.DT_CALCULO,
                installments: lstSelecionados
                    ?.map((lst) => lst.NumParc_Prc)
                    .join("/"),
                builtInterestRule: "I",
                interestPresentValue: 0,
                differenceType: "A",
                operationType: "R",
                paymentConditions: lstSelecionadosParcelasGeradas.map(
                    (lst) =>
                        ({
                            conditionTypeId: lst.idDescricao_par,
                            totalValue: lst.ValorReaj,
                            MaxInstallmentsNumber: Number(lst.quantidadePrc),
                            bearerId: lst.idPortadorPrc,
                            firstPayment: lst.Data_Prc,
                            matchMaturities: lst.operacaoCobrancaPrc
                                ? "S"
                                : "N",
                            indexerId: lst.idIndexadorPrc,
                            baseDate: lst.DtBase_Correcao,
                            interestType: lst.tipoJurosPrc,
                            interestPercentage: lst.percentualAoPeriodoPrc,
                            monthsGracePeriod: lst.carenciaPrc,
                            baseDateInterest: lst.dataBaseParaJurosPrc,
                        } as any)
                ),
            }).then(() => {
                props.setShow(false);
                ReloadHistorico();
            });
        }
    }

    return (
        <RegistrarAcordoSiengeContext.Provider
            value={{
                goToStep,
                step,
                selecionarParcelas,
                setLstSelecionados,
                lstSelecionados,
                parcelaReq,
                setParcelaReq,
                lstTodasParcelas,
                lstSelecionadosParcelasGeradas,
                setLstSelecionadosParcelasGeradas,
                SalvarSimulacao,
                RealizarRenegociacaoSiengeLoading,
            }}
        >
            <Wizard stepInfo={etapasInfo} unmountOnExit {...wizard}>
                <WizardStep stepKey="selecParcelas">
                    <SelecaoParcelas />
                </WizardStep>
                <WizardStep stepKey="condicoesPagamento">
                    <CondicoesPagamento />
                </WizardStep>
                <WizardStep stepKey="simularParcela">
                    <SimularParcela />
                </WizardStep>
            </Wizard>
        </RegistrarAcordoSiengeContext.Provider>
    );
}

export function useRegistrarAcordoSiengeContext() {
    const context = useContext(RegistrarAcordoSiengeContext);
    if (!context)
        throw new Error(
            "useRegistrarAcordoContext necessita de um RegistrarAcordoProvider em volta do que sera utilizado"
        );

    return context;
}
